import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ClipboardCheck, 
  Search, 
  User, 
  Calendar,
  Heart,
  Sparkles,
  Target,
  MessageSquare,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Users,
  BarChart3,
  TrendingUp,
  Bell,
  Mail,
  Settings2,
  Trash2,
  CheckCheck,
  X
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { ClassAverageBarChart, ProgressLineChart, CheckInFullDisplay, FruitsRadarChart, GlorifyingBarChart } from "@/components/CheckInCharts";

// Rating display component
const RatingDisplay = ({ label, value }) => {
  const getColor = (val) => {
    if (val <= 3) return "bg-red-100 text-red-700";
    if (val <= 5) return "bg-amber-100 text-amber-700";
    if (val <= 7) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };

  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-slate-600">{label}</span>
      <Badge className={getColor(value)}>{value}/10</Badge>
    </div>
  );
};

const getInitials = (name) => {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
};

// Single check-in row. In select mode, clicking toggles a checkbox instead of opening the detail dialog.
const CheckInCard = ({ checkIn, onClick, selectMode, selected, onToggleSelect }) => (
  <Card
    data-testid={`checkin-card-${checkIn.checkin_id}`}
    className={`hover:shadow-md transition-shadow cursor-pointer ${!checkIn.is_read_by_teacher ? "border-sky-300 bg-sky-50/40" : ""} ${selected ? "ring-2 ring-orange-400" : ""}`}
    onClick={() => (selectMode ? onToggleSelect(checkIn.checkin_id) : onClick())}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        {selectMode && (
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(checkIn.checkin_id)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`checkin-select-checkbox-${checkIn.checkin_id}`}
          />
        )}
        {!selectMode && !checkIn.is_read_by_teacher && (
          <span
            data-testid={`unread-dot-${checkIn.checkin_id}`}
            className="w-2.5 h-2.5 rounded-full bg-sky-500 flex-shrink-0"
            title="Unread"
          />
        )}
        <Avatar className="w-12 h-12">
          <AvatarFallback className="bg-slate-100 text-slate-700">
            {getInitials(checkIn.student_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 flex items-center gap-2">
            {checkIn.student_name}
          </p>
          <p className="text-sm text-slate-500">
            <Calendar className="w-3 h-3 inline mr-1" />
            {format(new Date(checkIn.created_at), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!checkIn.is_read_by_teacher && (
            <Badge data-testid={`unread-badge-${checkIn.checkin_id}`} className="bg-sky-500 hover:bg-sky-500 text-white">
              Unread
            </Badge>
          )}
          <div className="text-right hidden md:block">
            <p className="text-sm text-slate-600">Loved God</p>
            <Badge variant="outline">{checkIn.loved_well?.loved_god}/10</Badge>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-sm text-slate-600">Loved People</p>
            <Badge variant="outline">{checkIn.loved_well?.loved_people}/10</Badge>
          </div>
          {!checkIn.was_honest && (
            <Badge className="bg-amber-100 text-amber-700">
              <AlertCircle className="w-3 h-3 mr-1" />
              Not Honest
            </Badge>
          )}
          {!selectMode && <ChevronRight className="w-5 h-5 text-slate-400" />}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function CheckIns({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [checkIns, setCheckIns] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(searchParams.get('student') || 'all');
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [sendingAllReminders, setSendingAllReminders] = useState(false);
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [markingRead, setMarkingRead] = useState(false);
  const autoOpenedCheckinRef = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/checkins/unread-count`);
      setGlobalUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedStudent !== 'all' ? `?student_id=${selectedStudent}` : '';
      const [checkInsRes, pendingRes, studentsRes] = await Promise.all([
        axios.get(`${API}/checkins${params}`),
        axios.get(`${API}/checkins/pending-students`),
        axios.get(`${API}/users/students`)
      ]);

      setCheckIns(checkInsRes.data);
      setPendingStudents(pendingRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error("Error fetching check-ins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== "teacher") {
      navigate('/student');
      return;
    }
    fetchData();
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedStudent, navigate]);

  const handleDeleteCheckIn = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/checkins/${deleteTarget}`);
      toast.success("Check-in deleted");
      setDeleteTarget(null);
      setDetailOpen(false);
      setSelectedCheckIn(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete check-in");
    } finally {
      setDeleting(false);
    }
  };

  const filteredCheckIns = checkIns.filter(c => 
    c.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCheckInDetail = (checkIn) => {
    setSelectedCheckIn(checkIn);
    setDetailOpen(true);
    if (!checkIn.is_read_by_teacher) {
      axios.post(`${API}/checkins/${checkIn.checkin_id}/mark-read`).then(() => {
        setCheckIns(prev => prev.map(c => c.checkin_id === checkIn.checkin_id ? { ...c, is_read_by_teacher: true } : c));
        setGlobalUnreadCount(prev => Math.max(0, prev - 1));
      }).catch(() => {
        toast.error("Couldn't mark this check-in as read");
      });
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds([]);
  };

  const toggleSelectCheckIn = (checkinId) => {
    setSelectedIds(prev => prev.includes(checkinId) ? prev.filter(id => id !== checkinId) : [...prev, checkinId]);
  };

  const selectAllUnread = async () => {
    try {
      const params = selectedStudent !== 'all' ? `?student_id=${selectedStudent}` : '';
      const res = await axios.get(`${API}/checkins/unread-ids${params}`);
      setSelectedIds(res.data.checkin_ids);
    } catch (error) {
      toast.error("Couldn't load unread check-ins");
    }
  };

  const markSelectedAsRead = async () => {
    if (selectedIds.length === 0) return;
    setMarkingRead(true);
    try {
      const res = await axios.post(`${API}/checkins/mark-read-bulk`, { checkin_ids: selectedIds });
      setCheckIns(prev => prev.map(c => selectedIds.includes(c.checkin_id) ? { ...c, is_read_by_teacher: true } : c));
      setGlobalUnreadCount(prev => Math.max(0, prev - (res.data.marked_count ?? selectedIds.length)));
      toast.success(`Marked ${selectedIds.length} check-in${selectedIds.length === 1 ? '' : 's'} as read`);
      setSelectedIds([]);
      setSelectMode(false);
    } catch (error) {
      toast.error("Couldn't mark the selected check-ins as read");
    } finally {
      setMarkingRead(false);
    }
  };

  // Auto-open a check-in when arriving via a "View Check-In" link (?checkin_id=...) from a chat message
  useEffect(() => {
    const cid = searchParams.get('checkin_id');
    if (!cid || autoOpenedCheckinRef.current === cid || checkIns.length === 0) return;
    const match = checkIns.find(c => c.checkin_id === cid);
    if (match) {
      autoOpenedCheckinRef.current = cid;
      openCheckInDetail(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, checkIns]);

  const sendReminder = async (studentId) => {
    setSendingReminder(studentId);
    try {
      const response = await axios.post(`${API}/checkins/remind/${studentId}`);
      toast.success(response.data.message);
      // Refresh data to get updated reminder timestamp
      fetchData();
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const sendAllReminders = async () => {
    setSendingAllReminders(true);
    try {
      const response = await axios.post(`${API}/checkins/remind-all`);
      toast.success(response.data.message);
      // Refresh data to get updated reminder timestamps
      fetchData();
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast.error("Failed to send reminders");
    } finally {
      setSendingAllReminders(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (user?.role !== "teacher") {
    return null;
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Weekly Check-Ins
            </h1>
            <p className="text-slate-600 mt-1">
              Review student discipleship progress
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/checkin-editor')}
            className="shrink-0"
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Edit Check-In Content
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {students.length - (pendingStudents.pending_count || 0)}
                  </p>
                  <p className="text-sm text-slate-500">Submitted (Last 7 Days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={pendingStudents.pending_count > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pendingStudents.pending_count > 0 ? "bg-amber-200" : "bg-slate-100"
                }`}>
                  <AlertCircle className={`w-6 h-6 ${
                    pendingStudents.pending_count > 0 ? "text-amber-600" : "text-slate-400"
                  }`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {pendingStudents.pending_count || 0}
                  </p>
                  <p className="text-sm text-slate-500">Pending (7+ Days)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{checkIns.length}</p>
                  <p className="text-sm text-slate-500">Total Check-Ins</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-[500px]">
            <TabsTrigger value="all" className="relative" data-testid="all-checkins-tab">
              All Check-Ins
              {globalUnreadCount > 0 && (
                <Badge data-testid="unread-checkins-badge" className="ml-2 bg-sky-500 hover:bg-sky-500">{globalUnreadCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">
              <BarChart3 className="w-4 h-4 mr-1" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="pending" className="relative" data-testid="pending-tab">
              Pending
              {pendingStudents.pending_count > 0 && (
                <Badge className="ml-2 bg-amber-500">{pendingStudents.pending_count}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by student name..."
                  data-testid="checkin-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedStudent} onValueChange={(v) => { setSelectedStudent(v); setSelectedIds([]); }}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="student-filter-select">
                  <SelectValue placeholder="Filter by student" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Students</SelectItem>
                  {students.map((student) => (
                    <SelectItem key={student.user_id} value={student.user_id}>
                      {student.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant={selectMode ? "secondary" : "outline"}
                onClick={toggleSelectMode}
                data-testid="toggle-select-mode-btn"
              >
                {selectMode ? (<><X className="w-4 h-4 mr-1.5" /> Cancel</>) : (<><CheckCheck className="w-4 h-4 mr-1.5" /> Select</>)}
              </Button>
            </div>

            {/* Bulk select action bar */}
            {selectMode && (
              <div className="flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2.5" data-testid="bulk-select-bar">
                <span className="text-sm font-medium text-orange-800">
                  {selectedIds.length} selected
                </span>
                <Button size="sm" variant="ghost" onClick={selectAllUnread} data-testid="select-all-unread-btn">
                  Select all unread
                </Button>
                <div className="flex-1" />
                <Button
                  size="sm"
                  onClick={markSelectedAsRead}
                  disabled={selectedIds.length === 0 || markingRead}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="mark-selected-read-btn"
                >
                  {markingRead ? "Marking..." : `Mark ${selectedIds.length || ''} as Read`}
                </Button>
              </div>
            )}

            {/* Check-ins List */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
              </div>
            ) : filteredCheckIns.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No check-ins found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredCheckIns.map((checkIn) => (
                  <CheckInCard
                    key={checkIn.checkin_id}
                    checkIn={checkIn}
                    onClick={() => openCheckInDetail(checkIn)}
                    selectMode={selectMode}
                    selected={selectedIds.includes(checkIn.checkin_id)}
                    onToggleSelect={toggleSelectCheckIn}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6 mt-4">
            {checkIns.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No check-in data available for analytics</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Student Selector for Individual Charts */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      View Student Progress
                    </CardTitle>
                    <CardDescription>
                      Select a student to view their individual check-in trends
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                      <SelectTrigger className="w-full md:w-[300px]">
                        <SelectValue placeholder="Select a student" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Students (Class View)</SelectItem>
                        {students.map((student) => (
                          <SelectItem key={student.user_id} value={student.user_id}>
                            {student.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>

                {/* Individual Student Charts */}
                {selectedStudent !== 'all' && checkIns.length > 0 && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          {students.find(s => s.user_id === selectedStudent)?.name || 'Student'} - Progress Over Time
                        </CardTitle>
                        <CardDescription>
                          Track spiritual growth across {checkIns.length} check-in{checkIns.length !== 1 ? 's' : ''}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ProgressLineChart checkIns={checkIns} />
                      </CardContent>
                    </Card>

                    {checkIns.length > 0 && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-amber-500" />
                              Latest Fruits of Spirit
                            </CardTitle>
                            <CardDescription className="text-xs">
                              Most recent check-in scores
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <CheckInFullDisplay checkIn={checkIns[0]} />
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Target className="w-4 h-4 text-blue-500" />
                              Student Averages
                            </CardTitle>
                            <CardDescription className="text-xs">
                              Average across all their check-ins
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ClassAverageBarChart checkIns={checkIns} />
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </>
                )}

                {/* Class-Wide Analytics */}
                {selectedStudent === 'all' && (
                  <>
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          Class Averages
                        </CardTitle>
                        <CardDescription>
                          Average scores across all {checkIns.length} check-ins from all students
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ClassAverageBarChart checkIns={checkIns} />
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          Class Progress Over Time
                        </CardTitle>
                        <CardDescription>
                          Track overall class spiritual growth trends
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ProgressLineChart checkIns={checkIns} />
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="pending" className="space-y-4 mt-4">
            {pendingStudents.pending_students?.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <p className="text-slate-500">All students have submitted their check-ins!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Remind All Button */}
                {pendingStudents.pending_students?.length > 1 && (
                  <div className="flex justify-end">
                    <Button
                      onClick={sendAllReminders}
                      disabled={sendingAllReminders}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {sendingAllReminders ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Remind All ({pendingStudents.pending_students.length})
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                <div className="space-y-3">
                  {pendingStudents.pending_students?.map((student) => (
                    <Card key={student.user_id}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={student.picture} />
                            <AvatarFallback className="bg-amber-100 text-amber-700">
                              {getInitials(student.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">{student.name}</p>
                            <p className="text-sm text-slate-500">{student.email}</p>
                            {student.last_checkin_at ? (
                              <p className="text-xs text-slate-500 mt-1">
                                Last check-in: {formatTimeAgo(student.last_checkin_at)}
                              </p>
                            ) : (
                              <p className="text-xs text-amber-600 mt-1">No check-ins yet</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendReminder(student.user_id)}
                              disabled={sendingReminder === student.user_id}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              {sendingReminder === student.user_id ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mr-1"></div>
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Bell className="w-4 h-4 mr-1" />
                                  Remind
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/messages/${student.user_id}`)}
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Message
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Check-In Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedCheckIn && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-amber-600" />
                    Check-In Details
                  </DialogTitle>
                  <DialogDescription>
                    {selectedCheckIn.student_name} • {format(new Date(selectedCheckIn.created_at), 'MMMM d, yyyy')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Honesty Flag */}
                  {!selectedCheckIn.was_honest && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">Student was not fully honest</p>
                            <p className="text-sm text-amber-700 mt-1">
                              Reason: {selectedCheckIn.dishonesty_reason || "No reason provided"}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Loved Well Section */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Heart className="w-4 h-4 text-red-500" />
                        Love Assessment
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RatingDisplay label="Loved God" value={selectedCheckIn.loved_well?.loved_god} />
                      <RatingDisplay label="Loved People" value={selectedCheckIn.loved_well?.loved_people} />
                    </CardContent>
                  </Card>

                  {/* Fruits of Spirit */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Fruits of the Spirit
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <RatingDisplay label="Love" value={selectedCheckIn.fruits_of_spirit?.love} />
                      <RatingDisplay label="Joy" value={selectedCheckIn.fruits_of_spirit?.joy} />
                      <RatingDisplay label="Peace" value={selectedCheckIn.fruits_of_spirit?.peace} />
                      <RatingDisplay label="Patience" value={selectedCheckIn.fruits_of_spirit?.patience} />
                      <RatingDisplay label="Kindness" value={selectedCheckIn.fruits_of_spirit?.kindness} />
                      <RatingDisplay label="Goodness" value={selectedCheckIn.fruits_of_spirit?.goodness} />
                      <RatingDisplay label="Faithfulness" value={selectedCheckIn.fruits_of_spirit?.faithfulness} />
                      <RatingDisplay label="Gentleness" value={selectedCheckIn.fruits_of_spirit?.gentleness} />
                      <RatingDisplay label="Self-Control" value={selectedCheckIn.fruits_of_spirit?.self_control} />
                    </CardContent>
                  </Card>

                  {/* Glorifying God */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-500" />
                        Glorifying God
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RatingDisplay label="Use of Words" value={selectedCheckIn.glorifying_god?.use_of_words} />
                      <RatingDisplay label="Thought Life" value={selectedCheckIn.glorifying_god?.thought_life} />
                      <RatingDisplay label="Use of Time" value={selectedCheckIn.glorifying_god?.use_of_time} />
                      <RatingDisplay label="Use of Money" value={selectedCheckIn.glorifying_god?.use_of_money} />
                      <RatingDisplay label="Management of Temptations" value={selectedCheckIn.glorifying_god?.management_of_temptations} />
                    </CardContent>
                  </Card>

                  {/* Reflection */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-purple-500" />
                        Reflection Answers
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Biggest Fear</p>
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-1">
                          {selectedCheckIn.reflection?.biggest_fear || "No answer provided"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Biggest Temptation</p>
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-1">
                          {selectedCheckIn.reflection?.biggest_temptation || "No answer provided"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Biggest Victory</p>
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-1">
                          {selectedCheckIn.reflection?.biggest_victory || "No answer provided"}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Custom Question Responses */}
                  {selectedCheckIn.custom_responses && selectedCheckIn.custom_responses.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <ClipboardCheck className="w-4 h-4 text-indigo-500" />
                          Additional Questions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {selectedCheckIn.custom_responses.map((cr, idx) => (
                          <div key={idx}>
                            <p className="text-sm font-medium text-slate-700">{cr.question_text}</p>
                            <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-1">
                              {Array.isArray(cr.response_value)
                                ? cr.response_value.join(", ")
                                : cr.response_value === true
                                ? "Yes"
                                : cr.response_value === false
                                ? "No"
                                : cr.response_value || "No answer provided"}
                            </p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="flex justify-between mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-checkin-button"
                    onClick={() => setDeleteTarget(selectedCheckIn.checkin_id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/students/${selectedCheckIn.student_id}`)}
                    >
                      View Student Profile
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="reply-with-checkin-button"
                      onClick={() => navigate(`/messages/${selectedCheckIn.student_id}?checkin_id=${selectedCheckIn.checkin_id}`)}
                    >
                      Send Message
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteTarget !== null} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                Delete Check-In
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete this check-in? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" data-testid="cancel-delete-button" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
              <Button variant="destructive" data-testid="confirm-delete-button" onClick={handleDeleteCheckIn} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
